import React from 'react';
import { ChevronLeft, ChevronRight, Loader } from 'lucide-react';
import { PAGE_SIZE_OPTIONS } from '../hooks/usePageSize';

/**
 * Prev/next pagination with a page-size selector, for client-paginated card lists
 * (risks, remediations, risk register) and server-paginated lists (deals, business units).
 *
 * Changing the page size keeps the first currently-visible item on screen rather than
 * jumping back to page 1.
 *
 * @param {object} [scrollTargetRef] ref to the top of the list; a page change scrolls there
 *                 (falls back to the top of the window) so the new page starts at its first row
 * @param {boolean} [busy] server-paginated lists: a page fetch is in flight — disable the
 *                  controls and show a spinner so a double-click can't queue extra fetches
 */
const Pagination = ({ page, totalPages, totalItems, pageSize, onPageChange, onPageSizeChange, scrollTargetRef, busy = false, className = '' }) => {
    // Nothing to page through and no size worth choosing.
    if (totalItems <= PAGE_SIZE_OPTIONS[0]) return null;

    // The controls sit below a screen or more of tall cards; without this the user lands
    // mid-way down the new page. Instant, not smooth — from page 50 the animation is endless.
    const goToPage = (next) => {
        if (next === page) return;
        onPageChange(next);
        requestAnimationFrame(() => {
            const target = scrollTargetRef?.current;
            if (target) target.scrollIntoView({ block: 'start' });
            else window.scrollTo(0, 0);
        });
    };

    const handlePageSizeChange = (nextSize) => {
        const firstItemIndex = (page - 1) * pageSize;
        onPageSizeChange(nextSize);
        goToPage(Math.floor(firstItemIndex / nextSize) + 1);
    };

    const buttonClass = 'flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed';

    return (
        <div className={`flex flex-wrap items-center justify-between gap-3 mt-4 pt-4 border-t border-gray-200 ${className}`}>
            <div className="flex items-center gap-2">
                <label htmlFor="page-size-select" className="text-sm text-gray-600">Show</label>
                <select
                    id="page-size-select"
                    value={pageSize}
                    disabled={busy}
                    onChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
                    className="border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white text-gray-700 disabled:opacity-50"
                >
                    {PAGE_SIZE_OPTIONS.map(size => (
                        <option key={size} value={size}>{size}</option>
                    ))}
                </select>
                <span className="text-sm text-gray-600">per page</span>
            </div>

            <div className="flex items-center gap-3">
                <button
                    onClick={() => goToPage(Math.max(1, page - 1))}
                    disabled={page === 1 || busy}
                    className={buttonClass}
                >
                    <ChevronLeft className="h-4 w-4 mr-1" /> Previous
                </button>
                <span className="text-sm text-gray-600 flex items-center gap-1.5">
                    {busy && <Loader className="h-3.5 w-3.5 animate-spin" />}
                    Page {page} of {totalPages} ({totalItems} total)
                </span>
                <button
                    onClick={() => goToPage(Math.min(totalPages, page + 1))}
                    disabled={page >= totalPages || busy}
                    className={buttonClass}
                >
                    Next <ChevronRight className="h-4 w-4 ml-1" />
                </button>
            </div>
        </div>
    );
};

export default Pagination;
